import { Button, Col, Divider, Form, FormInstance, Row } from "antd";
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  forwardRef,
} from "react";
import ErrorBoundary from "../ErrorBoundary";
import widgetsTran from "./Widgets";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import styles from "./index.module.less";
const initialFormConfig = {
  labelCol: { span: 6 },
  wrapperCol: { span: 18 },
  layout: "horizontal",
  colon: false,
};
const initialFormItemConfig = {
  col: {
    span: 8,
  },
};
// type widgetType = 'Input' | 'Select'
export interface WidgetType {
  widgetType: string;
  isShow?: (formValue: any) => boolean;
  isDisabled?: (formValue: any) => boolean;
  [propName: string]: any;
}

interface ColType {
  span: number;
  push?: number;
  pull?: number;
  order?: number;
  offset?: number;
  flex?: string | number;
  [propName: string]: any;
}
export interface ItemType {
  label?: string;
  name: string;
  isShow?: (formValue: any) => boolean;
  col?: ColType;
  widget?: WidgetType;
  lists?: Array<ItemType>;
  [propName: string]: any;
}
interface PropsType {
  formItem: Array<ItemType>;
  formConfig?: any;
  formValue?: any;
  renderForm?: boolean;
  readOnly?: boolean; // 全局只读模式
  isNewRecord?: boolean; // 是否为新增记录
  isViewMode?: boolean; // 是否为查看模式（通过查看按钮进入）
  [propName: string]: any;
}

interface InternalPropsType extends PropsType {
  formRef?: any;
}

const DynamicForm: React.FC<InternalPropsType> = (props) => {
  const {
    formRef: propFormRef,
    formItem,
    formConfig,
    formValue,
    renderForm = true,
    readOnly = false,
    isNewRecord = true,
    isViewMode = false,
    children,
    ...otherProps
  } = props;
  const internalFormRef = useRef<FormInstance>();

  // 尝试从父级 Form 获取实例（如果存在）
  const parentFormInstance = Form.useFormInstance();

  const formRef = propFormRef || internalFormRef;
  const formInstance = useRef<FormInstance | null>(null);

  const [stateFormConfig, setStateFormConfig] = useState<any>({});
  const [stateFormItem, setStateFormItem] = useState<any[]>([]);
  const [stateFormValues, setStateFormValues] = useState<any>({});

  // 创建 mixinItemConfig 的 ref 用于避免循环依赖
  const mixinItemConfigRef = useRef<any>();

  const mixinOnValuesChange = useCallback(
    (changedValues: any, allValues: any) => {
      // 更新状态以触发重新渲染
      setStateFormValues(allValues);
      if (mixinItemConfigRef.current) {
        setStateFormItem(mixinItemConfigRef.current(formItem, allValues));
      }
      if (!formConfig) {
        return;
      }
      const { onValuesChange }: any = formConfig;
      if (onValuesChange) {
        onValuesChange(changedValues, allValues);
      }
    },
    [formItem, formConfig],
  );

  // 将 onValuesChange 暴露到全局作用域，以便自定义组件可以调用
  useEffect(() => {
    window.__dynamicFormOnValuesChange = mixinOnValuesChange;
    return () => {
      delete window.__dynamicFormOnValuesChange;
    };
  }, [mixinOnValuesChange]);
  const mixinFormConfig = useCallback(() => {
    return {
      ...initialFormConfig,
      ...formConfig,
      onValuesChange: mixinOnValuesChange,
    };
  }, [formConfig, mixinOnValuesChange]);
  const mixinWidgetConfig = useCallback(
    (widgetConfig: WidgetType | undefined, formValue: any = {}) => {
      // 修改widget的配置
      if (!!widgetConfig) {
        const { isShow, isDisabled, optionsGetter } = widgetConfig;
        if (typeof isShow === "function") {
          // 如果isShow执行后返回false
          let show = isShow(formValue);
          if (typeof show !== "boolean") {
          }
          if (!show) {
            return undefined;
          }
        }
        // 如果显式设置了 disabled: false，则不受 readOnly 影响
        let disabled =
          widgetConfig.disabled !== false
            ? widgetConfig.disabled || readOnly || false
            : false;
        if (typeof isDisabled === "function") {
          if (typeof isDisabled(formValue) !== "boolean") {
          } else {
            disabled = isDisabled(formValue) || readOnly;
          }
        }
        let config = { ...widgetConfig, disabled, isNewRecord, isViewMode };

        // 处理动态选项
        if (typeof optionsGetter === "function") {
          config.options = optionsGetter(formValue);
        }

        delete config.isShow;
        delete config.isDisabled;
        delete config.optionsGetter;
        return config;
      }
    },
    [readOnly, isNewRecord, isViewMode],
  );
  const mixinItemConfig = useCallback(
    (itemConfigs: ItemType[] = [], formValue: any = {}) => {
      // 修改Item配置
      let items = itemConfigs
        .filter((item) => {
          if (typeof item.isShow === "function") {
            let isShow = item.isShow(formValue);
            if (typeof isShow === "boolean") {
              return isShow;
            } else {
              return false;
            }
          }
          return true;
        })
        .map((item) => {
          let itemConfig = {
            ...initialFormItemConfig,
            ...item,
            widget: mixinWidgetConfig(item.widget, formValue),
          };
          // 处理动态 rules
          if (typeof itemConfig.rules === "function") {
            itemConfig.rules = itemConfig.rules(formValue);
          }
          delete itemConfig.isShow;
          return itemConfig;
        });
      return items;
    },
    [mixinWidgetConfig],
  );

  // 设置 mixinItemConfig 的 ref
  mixinItemConfigRef.current = mixinItemConfig;

  // Handle formValue changes
  useEffect(() => {
    if (formInstance.current && formValue) {
      formInstance.current.resetFields();
      formInstance.current.setFieldsValue(formValue);
      // 重要：更新 DynamicForm 的内部状态，以确保自定义渲染组件能获取到新值
      setStateFormValues(formValue);
    }
  }, [formValue]);

  // 监听表单字段变化（包括通过 setFieldsValue 的更新）
  useEffect(() => {
    if (!formInstance.current) return;

    const unsubscribe = formInstance.current?.subscribe?.(() => {
      const currentValues = formInstance.current?.getFieldsValue?.();
      if (currentValues) {
        setStateFormValues(currentValues);
      }
    });

    return () => {
      unsubscribe?.();
    };
  }, []);

  // Handle formItem changes
  useEffect(() => {
    setStateFormItem(
      mixinItemConfigRef.current
        ? mixinItemConfigRef.current(formItem, stateFormValues)
        : [],
    );
  }, [formItem, stateFormValues]);

  // Handle formConfig changes
  useEffect(() => {
    setStateFormConfig(mixinFormConfig());
  }, [formConfig, mixinFormConfig]);
  // Initial setup
  useEffect(() => {
    // 如果是嵌套模式且有父级 Form，使用父级 Form 实例
    if (!renderForm && parentFormInstance) {
      formInstance.current = parentFormInstance;
    } else {
      formInstance.current = formRef.current as FormInstance<any>;
    }

    if (formInstance.current && formValue) {
      formInstance.current.setFieldsValue(formValue);
    }
    setStateFormItem(
      mixinItemConfigRef.current
        ? mixinItemConfigRef.current(formItem, formValue)
        : [],
    );
    setStateFormConfig(mixinFormConfig());
  }, [renderForm, parentFormInstance]); // eslint-disable-line react-hooks/exhaustive-deps
  const renderFormItem = useCallback(({ widget, ...item }: any) => {
    // 渲染FormItem
    return <Form.Item {...item}>{widgetsTran(widget)}</Form.Item>;
  }, []);
  const renderCustom = useCallback(
    (custom: any, itemConfig?: any) => {
      // 渲染自定义内容
      // 获取当前字段值 - 优先级：formInstance > stateFormValues > custom.fieldValue
      let fieldValue;

      if (formInstance.current && custom.name) {
        try {
          fieldValue = formInstance.current.getFieldValue(custom.name);
        } catch (e) {
          // 如果获取失败，使用 stateFormValues
          fieldValue = stateFormValues?.[custom.name];
        }
      } else if (stateFormValues && custom.name) {
        // 如果 formInstance 还未初始化，尝试从 stateFormValues 获取
        fieldValue = stateFormValues[custom.name];
      }

      // 如果都没有，使用默认值
      if (fieldValue === undefined) {
        fieldValue = custom.fieldValue;
      }

      // 为自定义组件提供 onChange 回调
      const handleCustomChange = (newValue: any) => {
        if (formInstance.current && custom.name) {
          formInstance.current.setFieldValue(custom.name, newValue);
        }
      };

      const customContent = custom.render
        ? custom.render(custom.fieldName, custom.remove, fieldValue, {
            formRef: formInstance,
            onChange: handleCustomChange,
          })
        : null;

      // 如果提供了 itemConfig（包含 label 等），则使用 Form.Item 包裹
      if (itemConfig && itemConfig.label) {
        return <Form.Item {...itemConfig}>{customContent}</Form.Item>;
      }

      return customContent;
    },
    [stateFormValues, formInstance],
  );
  const renderFormList = useCallback(
    ({ list, name }: any) => {
      // 渲染Form.List
      list = mixinItemConfigRef.current ? mixinItemConfigRef.current(list) : [];
      return (
        <Form.List name={name}>
          {(fields, { add, remove }) => (
            <>
              {fields.map(
                (
                  field, // 这里面的配置项没有进行初始化
                  index,
                ) => (
                  <Row
                    key={field.name}
                    gutter={[8, 0]}
                    className={styles.listItemRow}
                  >
                    <Col flex="32px" className={styles.indexBadgeCol}>
                      <div className={styles.indexBadge}>{index + 1}</div>
                    </Col>
                    {list.map(({ widget, col, custom, ...item }: any) => {
                      const itemCol = { ...col, gutter: 0 };
                      return (
                        <Col
                          {...itemCol}
                          key={item.name}
                          className={styles.listItemCol}
                        >
                          {custom &&
                            renderCustom(
                              {
                                ...custom,
                                fieldName: field.name,
                                remove,
                              },
                              {
                                ...item,
                                name: [field.name, item.name],
                                fieldKey: [field.fieldKey, item.name],
                              },
                            )}
                          {widget &&
                            renderFormItem({
                              widget,
                              ...item,
                              name: [field.name, item.name],
                              fieldKey: [field.fieldKey, item.name],
                            })}
                        </Col>
                      );
                    })}
                    <Col flex="40px" className={styles.removeButtonCol}>
                      <Button
                        type="text"
                        onClick={() => remove(field.name)}
                        block
                        icon={<MinusCircleOutlined />}
                        style={{ display: "none" }}
                      >
                        {/* 添加 */}
                      </Button>
                    </Col>
                  </Row>
                ),
              )}
              <div>
                <Button
                  type="dashed"
                  onClick={() => add()}
                  icon={<PlusOutlined />}
                  className={styles.addButton}
                >
                  添加
                </Button>
              </div>
            </>
          )}
        </Form.List>
      );
    },
    [renderFormItem, renderCustom],
  );
  const renderDivider = useCallback((divider: any) => {
    // 渲染Divider
    return <Divider {...divider}>{divider.title}</Divider>;
  }, []);

  // 处理包裹容器的渲染
  const renderFormItemsContent = useCallback(() => {
    const result: React.ReactNode[] = [];
    let wrapperBuffer: any[] = [];
    let wrapperConfig: any = null;

    stateFormItem.forEach((item: any, index: number) => {
      const {
        widget,
        divider,
        custom,
        col,
        list,
        wrapperStart,
        wrapperEnd,
        ...formItem
      } = item;

      // 遇到包裹容器开始标记
      if (wrapperStart) {
        wrapperConfig = wrapperStart;
        return;
      }

      // 遇到包裹容器结束标记
      if (wrapperEnd) {
        if (wrapperBuffer.length > 0) {
          // 将缓存的字段包裹在一个 Col 内的 div 中，保持 Row 布局
          result.push(
            <Col span={24} key={`wrapper-${index}`}>
              <div
                className={wrapperConfig?.className}
                style={wrapperConfig?.style}
              >
                <Row>
                  {wrapperBuffer.map((bufferedItem, idx) =>
                    React.cloneElement(bufferedItem, {
                      key: bufferedItem.key || `buffered-${idx}`,
                    }),
                  )}
                </Row>
              </div>
            </Col>,
          );
          wrapperBuffer = [];
          wrapperConfig = null;
        }
        return;
      }

      // 渲染单个字段
      const fieldNode = (
        <Col {...col} key={formItem.name}>
          {divider && renderDivider(divider)}
          {custom &&
            renderCustom(
              { ...custom, name: custom.name || formItem.name },
              formItem,
            )}
          {widget && renderFormItem({ widget, ...formItem })}
          {list && renderFormList({ list, ...formItem })}
        </Col>
      );

      // 如果在包裹容器内，添加到缓冲区
      if (wrapperConfig !== null) {
        wrapperBuffer.push(fieldNode);
      } else {
        result.push(fieldNode);
      }
    });

    return result;
  }, [
    stateFormItem,
    renderDivider,
    renderCustom,
    renderFormItem,
    renderFormList,
  ]);

  const renderFormItems = useCallback(() => {
    return renderFormItemsContent();
  }, [renderFormItemsContent]);

  const formContent = <Row>{renderFormItems()}</Row>;

  // 如果 renderForm 为 false，只渲染表单内容（用于嵌套在其他 Form 中的场景）
  // 此时必须由外层 Form 提供 Form 上下文
  if (!renderForm) {
    return (
      <div className={styles.container}>
        <ErrorBoundary>
          {formContent}
          <div>{children}</div>
        </ErrorBoundary>
      </div>
    );
  }

  // 否则渲染完整的 Form 组件（默认行为）
  return (
    <div className={styles.container}>
      <ErrorBoundary>
        <Form
          className={styles.form}
          ref={formRef}
          {...stateFormConfig}
          {...otherProps}
        >
          {formContent}
          <div>{children}</div>
        </Form>
      </ErrorBoundary>
    </div>
  );
};
const ForwardedDynamicForm = forwardRef<any, PropsType>((props, ref) => {
  return <DynamicForm {...(props as any)} formRef={ref} />;
});

export default ForwardedDynamicForm;
